/**
 * The step an input moves in. It comes from inputs.step, which an author sets per input
 * (0.001, 0.01, 0.1, 1, 5, 10 ...). Null or a bad value falls back to the input type's old default.
 */
export function resolveStep(step: number | string | null | undefined, fallback: number): number {
    const parsed = typeof step === "string" ? Number(step) : step;
    return typeof parsed === "number" && Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** How many decimal places a step needs, so 0.101 keeps three and 5 keeps none. */
export function stepDecimals(step: number): number {
    if (!Number.isFinite(step) || Number.isInteger(step)) return 0;
    const text = step.toString();
    if (text.includes("e-")) return Number(text.split("e-")[1]);
    return text.split(".")[1]?.length ?? 0;
}

/** Rounds away floating point drift, for example 0.1 + 0.2 becomes 0.3. */
export function roundToDecimals(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}

/** Move one step up or down from a value and keep the result tidy. */
export function nudgeByStep(
    current: number,
    direction: -1 | 1,
    step: number,
    bounds: { min?: number; max?: number } = {},
    minDecimals = 0
): number {
    const decimals = Math.max(stepDecimals(step), minDecimals);
    const next = roundToDecimals(current + direction * step, decimals);
    return Math.min(Math.max(next, bounds.min ?? Number.NEGATIVE_INFINITY), bounds.max ?? Number.POSITIVE_INFINITY);
}
